import Vue from "vue";
import Router from "vue-router";
import * as Sentry from "@sentry/vue";
import FipaListener from "@/utils/listenerUtility";
import routes from "./routes";
import auth from "@/utils/auth";
Vue.use(Router);

import { routerScrollState } from "@composables/route_scroll.ts";

const lastPositions = {};
const timeouts = {};
const router = new Router({
  mode: "history",
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (sessionStorage.getItem("ignoreScrollBehavior") === "true") {
      sessionStorage.removeItem("ignoreScrollBehavior");
      return;
    }

    // do not scroll if hash is dialog which means dialog is open
    if (to.hash == "#dialog" || from.hash == "#dialog") {
      return;
    }

    if (savedPosition) {
      if (to.hash && lastPositions[to.name + "-" + to.hash]) {
        return lastPositions[to.name + "-" + to.hash];
      } else if (lastPositions[to.name]) {
        return lastPositions[to.name];
      }
      return savedPosition;
    } else if (to.meta?.anchors && !to.hash) {
      // if user if logged in and there isn't specific anchor defined then just don't scroll
      if (auth.tokenStatus && !to.meta.anchors[from.name]) {
        return { x: 0, y: 0 };
      }

      return {
        selector: to.meta.anchors[from.name] ?? to.meta.anchors.default,
        offset: { x: 0, y: 48 }
      };
    } else if (to.meta?.anchor && !to.hash) {
      return {
        selector: to.meta.anchor,
        offset: { x: 0, y: 48 }
      };
    } else {
      return {
        x: 0,
        y: 0
      };
    }
  }
});

router.beforeEach(async (to, from, next) => {
  if (to?.params?.routeScrollState) {
    routerScrollState.routeScroll = to.params.routeScrollState;
    delete to.params.routeScrollState;
  }

  const userAuthenticated = auth.tokenStatus;
  // If user logged trough 401 page and hits back, then further back
  if (userAuthenticated && to.name === "401") {
    history.back();
    next();
    return;
  }

  if (to.meta.requireAuthorize && !userAuthenticated) {
    const params = JSON.stringify({ name: to.name, params: to.params });
    sessionStorage.setItem("redirection", params);

    next({ name: "401" });
    return;
  } else if (to.name === "401" && to.params?.returnPage?.name) {
    sessionStorage.setItem(
      "redirection",
      JSON.stringify({
        name: to.params.returnPage.name,
        params: to.params.returnPage.params
      })
    );
  }

  if (to.hash == "#dialog") {
    next();
    return;
  }

  if (from.hash) {
    lastPositions[from.name + "-" + from.hash] = {
      x: 0,
      y: window.scrollY
    };
  } else {
    lastPositions[from.name] = {
      x: 0,
      y: window.scrollY
    };
  }

  if (window.adn && typeof window.adn.clearOut === "function") {
    window.adn.clearOut();
  }

  if (!timeouts[to.name]) timeouts[to.name] = [];

  timeouts[to.name].push(
    setTimeout(() => {
      FipaListener.emit("routeIsLoading");
    }, 2000)
  );

  next();
});

router.afterEach((to, from) => {
  if (timeouts[to.name]) {
    timeouts[to.name] = timeouts[to.name].filter(v => {
      clearTimeout(v);
      return false;
    });
  }
  if (timeouts[from.name]) {
    timeouts[from.name] = timeouts[from.name].filter(v => {
      clearTimeout(v);
      return false;
    });
  }

  FipaListener.emit("routeHasCompletedLoading");

  if (window.Beacon) {
    window.Beacon("event", {
      type: "page-viewed",
      url: window.location.href,
      title: window.document.title || process.env.VUE_APP_TITLE || "FIPA"
    });
    window.Beacon("suggest");
  }
});

router.onError(error => {
  if (error.name === "ChunkLoadError") {
    FipaListener.emit("routerChunkErrorLoad");
    Sentry.withScope(scope => {
      scope.setLevel("warning");
      scope.setTag("network_error", "chunkFail");
      scope.setContext("Chunk info", error);
      Sentry.captureMessage("Encountered chunk error");
    });
  }
});

export default router;
